/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "../../../../apiClient";
import DataTable from "examples/Tables/DataTable";

const AdvertisementPage = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/advertisement");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching advertisement data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      setConfirmDialog({
        open: true,
        title:
          "Are you sure you want to " +
          (status === "Active" ? "deactivate" : "activate") +
          " this advertisement?",
        onConfirm: async () => {
          try {
            await apiClient.put(`/admin/advertisement/${id}`, { status: status });
            await fetchData(); // Refresh data while staying on the current page
            // Keep current page after refresh
          } catch (error) {
            console.error("Error updating advertisement status:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error activating advertisement:", error);
    }
  };

  // Open the confirmation dialog
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  // Close the confirmation dialog without deleting
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteId(null);
  };

  // Handle delete action
  const handleConfirmDelete = () => {
    try {
      apiClient.delete(`/admin/advertisement/${deleteId}`).then((response) => {
        if (response.status === 200) {
          handleCloseDialog();
          fetchData();
        }
      });
    } catch (error) {
      console.error("Error deleting advertisement:", error);
    }
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    console.log("Edit advertisement with ID:", id);
    editData(id);
  };

  const columns = [
    { Header: "Image", accessor: "image", width: "25%", align: "left" },
    { Header: "Title", accessor: "title", width: "25%", align: "left" },
    { Header: "Link", accessor: "link", width: "25%", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const rows = data.map((item) => ({
    image: (
      <MDBox display="flex" alignItems="center">
        <MDAvatar
          alt="advertisement"
          src={item.image}
          sx={{ width: 56, height: 56, borderRadius: "md" }}
        />
      </MDBox>
    ),
    title: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.title}
      </MDTypography>
    ),
    link: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.link}
      </MDTypography>
    ),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={item.status}
          color={item.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(item)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(item._id, item.status)}
          color={item.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDeleteClick(item._id)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));

  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  return (
    <>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this item?</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <DataTable
        table={{ columns, rows }}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        noEndBorder
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderBottom: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            padding: "16px 24px",
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
            sx={{ color: darkMode ? "#ccc" : "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
AdvertisementPage.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default AdvertisementPage;
